import React, { useEffect, useRef, useState } from "react";
import { DEMO_COLORS, fmtBRL, fmtInt } from "./demoStyles";

/**
 * Barra superior com KPIs vivos. Anima os números quando mudam
 * para dar sensação de operação em tempo real.
 */
export default function LiveKpisBar({ kpis }) {
  if (!kpis) {
    return (
      <div
        data-testid="live-kpis-loading"
        style={{
          padding: "10px 20px",
          background: DEMO_COLORS.panel,
          borderBottom: `1px solid ${DEMO_COLORS.divider}`,
          color: DEMO_COLORS.textDim,
          fontSize: 11,
          textAlign: "center",
        }}
      >
        Aguardando métricas...
      </div>
    );
  }

  return (
    <div
      data-testid="live-kpis-bar"
      style={{
        display: "flex",
        gap: 0,
        background: DEMO_COLORS.panel,
        borderBottom: `1px solid ${DEMO_COLORS.divider}`,
        padding: "8px 20px",
        overflowX: "auto",
      }}
    >
      <KpiPill
        label="Agendamentos"
        value={fmtInt(kpis.appointments_total)}
        delta={kpis.appointments_24h ? `+${kpis.appointments_24h} hoje` : null}
        color={DEMO_COLORS.success}
        testid="kpi-appointments"
      />
      <KpiPill
        label="Conversas ativas"
        value={fmtInt(kpis.active_conversations)}
        color={DEMO_COLORS.text}
        testid="kpi-conversations"
      />
      <KpiPill
        label="Leads quentes 🔥"
        value={fmtInt(kpis.hot_leads)}
        color={DEMO_COLORS.danger}
        testid="kpi-hot-leads"
      />
      <KpiPill
        label="Clientes reativados"
        value={fmtInt(kpis.reactivated_customers)}
        color={DEMO_COLORS.accent}
        testid="kpi-reactivated"
      />
      <KpiPill
        label="Mensagens 24h"
        value={fmtInt(kpis.messages_24h)}
        color={DEMO_COLORS.text}
        testid="kpi-messages"
      />
      <KpiPill
        label="Resposta média"
        value={`${kpis.avg_response_time_s}s`}
        color={DEMO_COLORS.textDim}
        testid="kpi-response-time"
      />
      <KpiPill
        label="Receita potencial"
        value={fmtBRL(kpis.revenue_potential_brl)}
        delta={kpis.revenue_24h_brl ? `${fmtBRL(kpis.revenue_24h_brl)} hoje` : null}
        color={DEMO_COLORS.accent}
        highlight
        testid="kpi-revenue"
      />
    </div>
  );
}

function KpiPill({ label, value, delta, color, testid, highlight }) {
  const [prev, setPrev] = useState(value);
  const [flash, setFlash] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (value !== prev) {
      setFlash(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setFlash(false), 900);
      setPrev(value);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, prev]);

  return (
    <div
      data-testid={testid}
      style={{
        minWidth: 130,
        padding: "6px 14px",
        borderRight: `1px solid ${DEMO_COLORS.divider}`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        transition: "background 400ms ease",
        background: flash ? "rgba(245, 158, 11, 0.1)" : "transparent",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: DEMO_COLORS.textDim,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: highlight ? 17 : 15,
          fontWeight: highlight ? 700 : 600,
          color: color || DEMO_COLORS.text,
          transition: "color 300ms ease",
        }}
      >
        {value}
      </div>
      {delta && (
        <div
          style={{
            fontSize: 9,
            color: DEMO_COLORS.textDim,
            marginTop: -1,
          }}
        >
          {delta}
        </div>
      )}
    </div>
  );
}
